import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { 
  Building, 
  MapPin, 
  Phone, 
  Globe, 
  Settings, 
  Users, 
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Target,
  Zap
} from "lucide-react";
import { BusinessDetails } from "@/types/index";
import GooglePlacesMapViewer from "@/components/ui/google-places-map-viewer";
import { 
  extractBusinessInfoFromPlace, 
  isValidBusinessPlace, 
  ExtractedBusinessInfo,
  BusinessCategory,
  getBusinessFeatureRecommendations
} from '@/lib/integrations/google-places-utils';
import BusinessDetailsStep from "../onboarding/steps/BusinessDetailsStep";
import TeamSetupStep from "../onboarding/steps/TeamSetupStep";

/**
 * Generate favicon URL from a website URL using Google's favicon service
 */
function getFaviconUrl(website: string | null | undefined): string | null {
  if (!website) return null;
  try {
    const url = new URL(website);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=32`;
  } catch {
    return null;
  }
}

const businessSchema = z.object({
  name: z.string().min(2, "Business name must be at least 2 characters"),
  description: z.string().optional(),
  address: z.string().min(5, "Please enter a valid address"),
  website: z.string().url("Please enter a valid website URL").optional().or(z.literal("")),
  phone: z.string().min(5, "Please enter a valid phone number").optional().or(z.literal("")),
  google_place_id: z.string().optional(),
});

export type BusinessFormValues = z.infer<typeof businessSchema>;

export interface OnboardingData {
  businessDetails: BusinessFormValues;
  businessInfo: ExtractedBusinessInfo | null;
  selectedFeatures: string[];
  teamMembers: Array<{
    email: string;
    role_id: string;
    name: string;
  }>;
  marketingPreferences: {
    enableReviews: boolean;
    enableLoyalty: boolean;
    enableNotifications: boolean;
  };
}

const ONBOARDING_STEPS = [
  { id: 'business-details', title: 'Business Details', icon: Building },
  { id: 'team', title: 'Team Setup', icon: Users },
];

interface CreateBusinessTabProps {
  onBusinessCreated: (newBusiness: BusinessDetails) => void;
}

const CreateBusinessTab: React.FC<CreateBusinessTabProps> = ({ onBusinessCreated }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkLimit, subscriptionSummary, isStripeAvailable } = useSubscription();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true); // Add checking state
  const [canCreateBusiness, setCanCreateBusiness] = useState(false); // Add permission state
  const [showManualForm, setShowManualForm] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    businessDetails: {
      name: "",
      description: "",
      address: "",
      website: "",
      phone: "",
      google_place_id: "",
    },
    businessInfo: null,
    selectedFeatures: [],
    teamMembers: [],
    marketingPreferences: {
      enableReviews: true,
      enableLoyalty: true,
      enableNotifications: true,
    },
  });

  const form = useForm<BusinessFormValues>({
    resolver: zodResolver(businessSchema),
    defaultValues: onboardingData.businessDetails,
  });

  // Check on mount if user can create a business (before loading Google API)
  useEffect(() => {
    const checkBusinessEligibility = async () => {
      if (!user) {
        setChecking(false);
        setCanCreateBusiness(false);
        return;
      }

      try {
        // Check if user already has a business
        const { data: existingBusinesses, error: checkError } = await supabase
          .from("business_users")
          .select("business_id")
          .eq("user_id", user.id)
          .limit(1);

        console.log("Pre-load business check - User ID:", user.id);
        console.log("Pre-load business check - Existing businesses:", existingBusinesses);

        if (checkError) {
          console.error("Error checking existing businesses on mount:", checkError);
          toast.error("Error", {
            description: "Could not verify business eligibility. Please refresh the page.",
          });
          setChecking(false);
          setCanCreateBusiness(false);
          return;
        }

        if (existingBusinesses && existingBusinesses.length > 0) {
          console.log("BLOCKING on mount: User already has", existingBusinesses.length, "business(es)");
          toast.error("Business Limit Reached", {
            description: "You can only have one business at this time. This limitation may be removed in the future.",
          });
          setChecking(false);
          setCanCreateBusiness(false);
          return;
        }

        console.log("Pre-load business check passed - user can create a business");
        setCanCreateBusiness(true);
      } catch (error) {
        console.error("Error in pre-load business check:", error);
        toast.error("Error", {
          description: "Could not verify business eligibility. Please refresh the page.",
        });
        setCanCreateBusiness(false);
      } finally {
        setChecking(false);
      }
    };

    checkBusinessEligibility();
  }, [user]);

  // Handle subscription success from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription') === 'success') {
      toast.success("Welcome!", {
        description: "Your 14-day trial has started. Let's set up your business!",
      });
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleNext = () => {
    if (currentStep === 0) {
      // Validation for Business Details step
      const hasGooglePlaceData = onboardingData.businessInfo !== null;
      const formValues = form.getValues();
      
      if (!hasGooglePlaceData && !showManualForm) {
        // User hasn't selected a Google Place and hasn't switched to manual form
        toast.error("Business Selection Required", {
          description: "Please select your business from the map or enter details manually",
        });
        return;
      }
      
      if (showManualForm || hasGooglePlaceData) {
        // Validate required fields for manual entry or Google Place selection
        const hasRequiredFields = formValues.name && formValues.address;
        
        if (!hasRequiredFields) {
          toast.error("Required Fields Missing", {
            description: "Please enter at least business name and address",
          });
          return;
        }
        
        // Run form validation
        const isFormValid = form.trigger(['name', 'address']);
        if (!isFormValid) {
          toast.error("Form Validation Failed", {
            description: "Please fix the errors in the form",
          });
          return;
        }
      }
    }
    
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    try {
      if (!isValidBusinessPlace(place)) {
        toast.error("Invalid Business", {
          description: "Please select a valid business establishment",
        });
        return;
      }

      const businessInfo = extractBusinessInfoFromPlace(place);

      if (!businessInfo) {
        toast.error("Error Processing Place", {
          description: "Unable to extract business information from selected place",
        });
        return;
      }

      // Extract latitude and longitude from Google Places geometry
      let latitude: number | null = null;
      let longitude: number | null = null;

      if (place.geometry?.location) {
        // Check if location has lat() and lng() methods (Google Maps API v3)
        if (typeof place.geometry.location.lat === 'function') {
          latitude = place.geometry.location.lat();
          longitude = place.geometry.location.lng();
        } else if (typeof place.geometry.location.lat === 'number') {
          // Direct number properties (new Places API)
          latitude = place.geometry.location.lat;
          longitude = place.geometry.location.lng;
        }

        console.log('📍 Extracted coordinates from Google Places:', { latitude, longitude });
      }

      setOnboardingData(prev => ({
        ...prev,
        businessInfo: { ...businessInfo, latitude, longitude },
        businessDetails: {
          ...prev.businessDetails,
          name: businessInfo.name,
          address: businessInfo.address,
          phone: businessInfo.phone || "",
          website: businessInfo.website || "",
          google_place_id: businessInfo.placeId,
        }
      }));

      // Update form values
      form.setValue("name", businessInfo.name);
      form.setValue("address", businessInfo.address);
      form.setValue("phone", businessInfo.phone || "");
      form.setValue("website", businessInfo.website || "");
      form.setValue("google_place_id", businessInfo.placeId);

      // Auto-select recommended features based on business type
      const recommendations = getBusinessFeatureRecommendations(businessInfo.businessCategory);
      setOnboardingData(prev => ({
        ...prev,
        selectedFeatures: recommendations.essential,
      }));

      toast.success("Business Details Loaded", {
        description: `Detected as ${businessInfo.businessCategory} business with recommended features${latitude && longitude ? ' (location captured)' : ''}`,
      });
    } catch (error) {
      console.error("Error in handlePlaceSelect:", error);
      toast.error("Error Processing Place", {
        description: "An error occurred while processing the selected place",
      });
    }
  };

  const handleFinish = async () => {
    if (!user) {
      toast.error("Authentication Required", {
        description: "You must be logged in to create a business",
      });
      return;
    }

    setLoading(true);

    try {
      // Check if user already has a business
      const { data: existingBusinesses, error: checkError } = await supabase
        .from("business_users")
        .select("business_id")
        .eq("user_id", user.id)
        .limit(1);

      console.log("Business check - User ID:", user.id);
      console.log("Business check - Existing businesses:", existingBusinesses);
      console.log("Business check - Error:", checkError);

      if (checkError) {
        console.error("Error checking existing businesses:", checkError);
        toast.error("Error", {
          description: "Could not verify business eligibility. Please try again.",
        });
        setLoading(false);
        return;
      }

      if (existingBusinesses && existingBusinesses.length > 0) {
        console.log("BLOCKING: User already has", existingBusinesses.length, "business(es)");
        toast.error("Business Limit Reached", {
          description: "You can only have one business at this time. This limitation may be removed in the future.",
        });
        setLoading(false);
        return;
      }

      console.log("Business check passed - user can create a business");

      // Validate required fields
      if (!onboardingData.businessDetails.name || !onboardingData.businessDetails.address) {
        toast.error("Missing Required Fields", {
          description: "Please provide at least a business name and address",
        });
        setLoading(false);
        return;
      }

      // Check subscription limits before creating business (only if Stripe is available)
      if (isStripeAvailable) {
        const canCreateBusiness = await checkLimit('businesses', undefined, 1);
        if (!canCreateBusiness) {
          const currentCount = subscriptionSummary?.businesses_used || 0;
          const limit = subscriptionSummary?.businesses_limit;
          
          toast.error("Business Limit Reached", {
            description: limit 
              ? `You've reached your limit of ${limit} ${limit === 1 ? 'business' : 'businesses'}. Upgrade your plan to create more businesses.`
              : "Unable to create business. Please check your subscription status.",
          });
          setLoading(false);
          return;
        }
      } else {
        // Show a warning when Stripe is not available but allow creation
        console.warn('Creating business without subscription limit checks: Stripe not configured');
      }

      // Create business with location coordinates from Google Places
      const websiteUrl = onboardingData.businessDetails.website || null;
      const { data: businessData, error: businessError } = await supabase
        .from("businesses")
        .insert({
          user_id: user.id,
          name: onboardingData.businessDetails.name,
          address: onboardingData.businessDetails.address,
          website: websiteUrl,
          favicon_url: getFaviconUrl(websiteUrl),
          phone: onboardingData.businessDetails.phone || null,
          google_place_id: onboardingData.businessDetails.google_place_id || null,
          // Auto-save coordinates from Google Places if available
          latitude: (onboardingData.businessInfo as any)?.latitude || null,
          longitude: (onboardingData.businessInfo as any)?.longitude || null,
          location_source: (onboardingData.businessInfo as any)?.latitude ? 'auto' : null,
          location_last_updated: (onboardingData.businessInfo as any)?.latitude ? new Date().toISOString() : null,
        })
        .select()
        .single();

      if (businessError) throw businessError;
      
      if (!businessData) {
        throw new Error("Failed to create business - no data returned");
      }

      // Assign owner role
      const { error: ownerError } = await supabase
        .from("business_users")
        .insert({
          user_id: user.id,
          business_id: businessData.id,
          role: "owner",
        });

      if (ownerError) throw ownerError;

      // Create initial QR code
      const uniqueId = crypto.randomUUID();
      const uniquePath = `${Date.now()}-${businessData.id.substring(0, 8)}`;
      const { error: qrError } = await supabase
        .from("qr_codes")
        .insert([{
          id: uniqueId,
          business_id: businessData.id,
          name: `Default Review QR`,
          unique_url_path: uniquePath,
        }]);

      if (qrError) {
        console.error("Error creating QR code:", qrError);
      }

      // Save opening hours if available, otherwise create sensible defaults
      if (onboardingData.businessInfo?.openingHours?.length > 0) {
        const openingHoursWithBusinessId = onboardingData.businessInfo.openingHours.map(hour => ({
          ...hour,
          business_id: businessData.id,
        }));

        const { error: hoursError } = await supabase
          .from("business_opening_hours")
          .insert(openingHoursWithBusinessId);

        if (hoursError) {
          console.error("Error saving opening hours:", hoursError);
        }
      } else {
        // Create default opening hours when none are provided from Google Places
        console.log("No opening hours from Google Places, creating default business hours");
        const defaultHours = [
          { day_of_week: 'Monday', opening_time: '09:00:00', closing_time: '17:00:00', is_closed_all_day: false, is_24_hours: false },
          { day_of_week: 'Tuesday', opening_time: '09:00:00', closing_time: '17:00:00', is_closed_all_day: false, is_24_hours: false },
          { day_of_week: 'Wednesday', opening_time: '09:00:00', closing_time: '17:00:00', is_closed_all_day: false, is_24_hours: false },
          { day_of_week: 'Thursday', opening_time: '09:00:00', closing_time: '17:00:00', is_closed_all_day: false, is_24_hours: false },
          { day_of_week: 'Friday', opening_time: '09:00:00', closing_time: '17:00:00', is_closed_all_day: false, is_24_hours: false },
          { day_of_week: 'Saturday', opening_time: '09:00:00', closing_time: '17:00:00', is_closed_all_day: false, is_24_hours: false },
          { day_of_week: 'Sunday', opening_time: null, closing_time: null, is_closed_all_day: true, is_24_hours: false },
        ].map(hour => ({
          ...hour,
          business_id: businessData.id,
        }));

        const { error: defaultHoursError } = await supabase
          .from("business_opening_hours")
          .insert(defaultHours);

        if (defaultHoursError) {
          console.error("Error saving default opening hours:", defaultHoursError);
        }
      }

      // Auto-configure features based on business type
      if (onboardingData.businessInfo?.businessCategory) {
        const recommendations = getBusinessFeatureRecommendations(onboardingData.businessInfo.businessCategory);
        // Auto-enable essential features for the business type
        console.log("Auto-configuring features for", onboardingData.businessInfo.businessCategory, ":", recommendations.essential);
        // TODO: Save essential features to business_features table
      }

      // Auto-configure marketing preferences based on business type
      const marketingDefaults = {
        enableReviews: true, // Always enable reviews
        enableLoyalty: onboardingData.businessInfo?.businessCategory === BusinessCategory.RESTAURANT || 
                      onboardingData.businessInfo?.businessCategory === BusinessCategory.RETAIL,
        enableNotifications: true // Always enable basic notifications
      };
      console.log("Auto-configuring marketing preferences:", marketingDefaults);
      
      // TODO: Invite team members from onboardingData.teamMembers

      toast.success("Business Created Successfully!", {
        description: `${businessData.name} has been set up with your selected features`,
      });

      onBusinessCreated(businessData);
      
      // Reset the form after success
      setTimeout(() => {
        setCurrentStep(0);
        setOnboardingData({
          businessDetails: {
            name: "",
            description: "",
            address: "",
            website: "",
            phone: "",
            google_place_id: "",
          },
          businessInfo: null,
          selectedFeatures: [],
          teamMembers: [],
          marketingPreferences: {
            enableReviews: true,
            enableLoyalty: true,
            enableNotifications: true,
          },
        });
        form.reset();
      }, 2000);
      
    } catch (error) {
      console.error("Error creating business:", error);
      toast.error("Error Creating Business", {
        description: error instanceof Error ? error.message : "Please try again",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <BusinessDetailsStep
            form={form}
            onboardingData={onboardingData}
            setOnboardingData={setOnboardingData}
            onPlaceSelect={handlePlaceSelect}
            showManualForm={showManualForm}
            setShowManualForm={setShowManualForm}
          />
        );
      case 1:
        return (
          <TeamSetupStep
            onboardingData={onboardingData}
            setOnboardingData={setOnboardingData}
          />
        );
      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;

  const isNextButtonDisabled = () => {
    if (currentStep === 0) {
      const hasGooglePlaceData = onboardingData.businessInfo !== null;
      const formValues = form.getValues();
      
      // If neither Google Place selected nor manual form shown, disable
      if (!hasGooglePlaceData && !showManualForm) {
        return true;
      }
      
      // If manual form is shown or Google Place selected, check required fields
      if (showManualForm || hasGooglePlaceData) {
        return !formValues.name || !formValues.address;
      }
    }
    
    return false;
  };

  // Show loading state while checking eligibility
  if (checking) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 min-h-0 mb-4">
          <Card className="h-full flex flex-col items-center justify-center">
            <CardContent className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Checking business eligibility...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show error state if user cannot create business
  if (!canCreateBusiness) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 min-h-0 mb-4">
          <Card className="h-full flex flex-col items-center justify-center">
            <CardContent className="flex flex-col items-center space-y-6 text-center max-w-md">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <Building className="w-8 h-8 text-destructive" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Business Limit Reached</h3>
                <p className="text-muted-foreground">
                  You already have a business registered. At this time, each user can only have one business.
                </p>
                <p className="text-sm text-muted-foreground">
                  This limitation may be removed in future updates. Please contact support if you need multiple businesses.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Go to Dashboard</span>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">

      {/* Step Content */}
      <div className="flex-1 min-h-0 mb-4">
        <Card className="h-full flex flex-col">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <div className="flex items-center space-x-2 text-lg">
                {React.createElement(ONBOARDING_STEPS[currentStep].icon, { className: "w-5 h-5" })}
                <span>{ONBOARDING_STEPS[currentStep].title}</span>
              </div>
              {currentStep === 0 && (
                <div className="flex items-center space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowManualForm(true)}
                  >
                    Enter details manually
                  </Button>
                  <div className="text-right">
                    <p className="text-blue-600 dark:text-blue-400 text-xs mb-1">
                      Don't have a Google My Business profile set up yet?
                    </p>
                    <a
                      href="https://support.google.com/business/answer/2911778?hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline"
                    >
                      Learn how to create your Google My Business profile
                      <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto">
            {renderStep()}
            
            {/* Footer with Progress and Navigation */}
            <div className="mt-6 pt-4 border-t">
              <div className="space-y-3">
                {/* Step Indicators */}
                <div className="flex items-center justify-center space-x-2">
                  {ONBOARDING_STEPS.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = index === currentStep;
                    const isCompleted = index < currentStep;
                    
                    return (
                      <div
                        key={step.id}
                        className={`flex items-center space-x-1 px-2 py-1 rounded-md transition-colors ${
                          isActive 
                            ? 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200' 
                            : isCompleted 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Icon className="w-4 h-4" />
                        )}
                        <span className="text-xs font-medium hidden md:block">
                          {step.title}
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                {/* Navigation with Progress Bar */}
                <div className="flex items-center w-full">
                  {/* Left 10% - Previous Button */}
                  <div className="w-[10%] flex justify-start">
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      disabled={currentStep === 0}
                      className="flex items-center space-x-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back</span>
                    </Button>
                  </div>
                  
                  {/* Center 80% - Progress Bar */}
                  <div className="w-[80%] flex items-center justify-center space-x-4 px-4">
                    <Progress value={progress} className="flex-1 h-2 !bg-gray-200 dark:!bg-gray-700" />
                  </div>
                  
                  {/* Right 10% - Next Button */}
                  <div className="w-[10%] flex justify-end">
                    {currentStep < ONBOARDING_STEPS.length - 1 ? (
                      <Button
                        onClick={handleNext}
                        disabled={isNextButtonDisabled()}
                        className="flex items-center space-x-2"
                      >
                        <span>Next</span>
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        onClick={handleFinish}
                        disabled={loading}
                        className="flex items-center space-x-2"
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Creating...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span>Create Business</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateBusinessTab;